/** Font evidence is a candidate for matching the image, never an instruction
 * to replace the type on the ad with the brand's preferred typeface. */
export interface CreativeFontCandidate { family: string; weight?: string }
export interface CreativeFontFace { family: string; src: string; weight?: string; style?: string; unicodeRange?: string }
export interface CreativeFontEvidence {
  fontCandidates?: CreativeFontCandidate[];
  fontFaces?: CreativeFontFace[];
}
const faceLoads = new Map<string, Promise<boolean>>();
const runtimeFamilies = new Map<string, string>();

/** CSS font registrations are global to the page. Scope each source set to
 * its own runtime family so two brands called "Display" cannot replace one
 * another's glyphs. The document always stores the original display name. */
export function creativeFontFamily(family: string, evidence: CreativeFontEvidence = {}): string {
  const faces = (evidence.fontFaces ?? []).filter(face => face.family === family);
  if (!faces.length) return family;
  const key = JSON.stringify([family, faces.map(face => JSON.stringify([
    face.src, face.weight || '400', face.style || 'normal', face.unicodeRange || '',
  ])).sort()]);
  let alias = runtimeFamilies.get(key);
  if (!alias) {
    alias = `AdmojiCreativeFont${runtimeFamilies.size + 1}`;
    runtimeFamilies.set(key, alias);
  }
  return alias;
}

export async function loadCreativeFontFaces(evidence: CreativeFontEvidence): Promise<Set<string>> {
  const loaded = new Set<string>();
  await Promise.all((evidence.fontFaces ?? []).map(async spec => {
    const alias = creativeFontFamily(spec.family, evidence);
    const key = JSON.stringify([alias, spec]);
    let pending = faceLoads.get(key);
    if (!pending) {
      pending = (async () => {
        try {
          const src = /^(?:https?:\/\/|data:font\/|data:application\/)/i.test(spec.src) ? `url(${JSON.stringify(spec.src)})` : spec.src;
          const face = new FontFace(alias, src, { weight: spec.weight || '400', style: spec.style || 'normal', ...(spec.unicodeRange ? { unicodeRange: spec.unicodeRange } : {}) });
          let timer: ReturnType<typeof setTimeout>;
          const timeout = new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), 8000); });
          const result = await Promise.race([face.load(), timeout]).finally(() => clearTimeout(timer));
          if (!result) return false;
          document.fonts.add(face);
          return true;
        } catch { return false; }
      })();
      faceLoads.set(key, pending);
    }
    if (await pending) loaded.add(spec.family);
    else faceLoads.delete(key);
  }));
  return loaded;
}

export function creativeFontWeight(style: string): number {
  return Number(style.match(/\b([1-9]\d{2})\b/)?.[1]) || (style.includes('bold') ? 700 : 400);
}

export function fontFaceMatchesVariant(face: {weight?: string; style?: string}, weight: number, style: string): boolean {
  const normalized = (face.weight || '400').replace('normal','400').replace('bold','700').trim().split(/\s+/).map(Number);
  const weightMatches = normalized.length === 2 ? weight >= normalized[0] && weight <= normalized[1] : normalized[0] === weight;
  return weightMatches && (face.style || 'normal') === style;
}
const variantStylesheets = new Map<string, Promise<boolean>>();

/** Loading a family name alone can silently synthesize bold/italic. Require
 * a real matching face, including custom/variable face descriptors. */
export async function loadCreativeFontVariant(family: string, fontStyle: string, evidence: CreativeFontEvidence = {}, text = 'BESbswy'): Promise<boolean> {
  const weight = creativeFontWeight(fontStyle);
  const style = fontStyle.includes('italic') ? 'italic' : 'normal';
  const custom = (evidence.fontFaces ?? []).filter(face => face.family === family);
  if (custom.length) {
    if (!custom.some(face => fontFaceMatchesVariant(face, weight, style))) return false;
    await loadCreativeFontFaces(evidence);
  } else {
    const key = JSON.stringify([family,weight,style]);
    let ready = variantStylesheets.get(key);
    if (!ready) {
      ready = new Promise<boolean>(resolve => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        // Each request asks for ONE supported variant; a nonexistent 900
        // face must not invalidate a single-weight family's 400 request.
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g,'+')}:ital,wght@${style === 'italic' ? 1 : 0},${weight}&display=swap`;
        const timer = setTimeout(()=>resolve(false),8000);
        link.onload = ()=>{clearTimeout(timer);resolve(true);};
        link.onerror = ()=>{clearTimeout(timer);resolve(false);};
        document.head.appendChild(link);
      });
      variantStylesheets.set(key,ready);
    }
    if (!await ready) {variantStylesheets.delete(key);return false;}
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const faces = await Promise.race([
      document.fonts.load(`${style} ${weight} 100px ${JSON.stringify(creativeFontFamily(family,evidence))}`,text),
      new Promise<FontFace[]>(resolve=>{timer=setTimeout(()=>resolve([]),8000);}),
    ]);
    return faces.length > 0 && faces.every(face=>face.status==='loaded' && fontFaceMatchesVariant(face,weight,style));
  } catch {return false;}
  finally {if(timer) clearTimeout(timer);}
}

export async function requireCreativeTextFonts(doc: CreativeFontEvidence & {texts: Array<{struck?: boolean; fontFamily: string; fontStyle: string; text: string}>}): Promise<void> {
  await Promise.all(doc.texts.filter(t=>t.struck).map(async t=>{
    if (!await loadCreativeFontVariant(t.fontFamily,t.fontStyle,doc,t.text))
      throw new Error(`Could not load ${t.fontFamily} (${t.fontStyle}). The text has not been changed. Please choose an available font or retry.`);
  }));
}

export function hasLoadedCreativeFontVariant(family: string, weight: number, style: string, evidence: CreativeFontEvidence = {}): boolean {
  const alias = creativeFontFamily(family,evidence);
  let found = false;
  document.fonts.forEach(face => {
    if (face.family.replace(/^['"]|['"]$/g,'') === alias && face.status === 'loaded' && fontFaceMatchesVariant(face,weight,style)) found = true;
  });
  return found;
}
